// A per-day cache of the /metrics ledger in the browser's IndexedDB, so the
// Dashboard does not read the HubSpot mirror on every load (2026-10-13, user
// request: "save in browser indexed db keyed by date").
//
// WHAT IS KEPT. One row per (actor, scope, Chicago day): the whole /metrics
// body the page last read, with the time it was read. A load looks up TODAY's
// row and paints from it at once. The row is read again from the mirror only
// when it is older than REFRESH_AFTER_MS (three of the mirror's five-minute
// sync cycles), and then in the background — the page keeps showing the
// cached numbers until the fresh ones arrive. A new day has no row, so the
// first load of a day reads the mirror; so does Refresh.
//
// WHY A DAY KEY, NOT A TTL ALONE. Yesterday's row must never stand in for
// today: every range on the chart ends "today", and a row keyed by day cannot
// be mistaken for a newer day's. Rows older than KEEP_DAYS are pruned on write.
//
// NOTHING HERE THROWS. IndexedDB is absent or refused in some private windows
// and sandboxes; every call catches its own failure and answers as if the cache
// were empty, so the worst case is exactly the page's behaviour before this
// file existed: one read per load. The reading itself (hubspotMetrics) is the
// caller's — this module only remembers what it was handed.

// Its own database, beside leadsCache.js's 'revailosales-leads': two modules
// opening one database with different stores and versions would race each
// other's upgrades.
const DB_NAME = 'revailosales-metrics'
const DB_VERSION = 1
const STORE = 'metrics'

export const REFRESH_AFTER_MS = 15 * 60 * 1000
export const KEEP_DAYS = 7

export function cacheKey({ actor, scope, day }) {
  return `${String(actor || '').toLowerCase()}|${scope || 'actor'}|${day}`
}

/**
 * Whether a cached row is old enough to read the mirror again. `savedAt` is
 * the epoch ms the row was written; a missing/garbled stamp counts as old.
 */
export function isStale(savedAt, now = Date.now(), after = REFRESH_AFTER_MS) {
  const at = Number(savedAt)
  if (!Number.isFinite(at) || at <= 0) return true
  return now - at >= after
}

function idb() {
  try {
    return typeof indexedDB === 'undefined' ? null : indexedDB
  } catch {
    return null
  }
}

// One open request per page; a failed open leaves the cache disabled for the
// page's life rather than retrying (and failing) on every call.
let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise
  const factory = idb()
  if (!factory) return (dbPromise = Promise.resolve(null))
  dbPromise = new Promise((resolve) => {
    try {
      const req = factory.open(DB_NAME, DB_VERSION)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' })
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
  return dbPromise
}

function request(db, mode, run) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode)
      const store = tx.objectStore(STORE)
      const req = run(store)
      if (!req) return resolve(null)
      req.onsuccess = () => resolve(req.result ?? null)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

/** The cached row for `key` — { key, day, savedAt, body } — or null. */
export async function readCached(key) {
  const db = await openDb()
  if (!db) return null
  const row = await request(db, 'readonly', (store) => store.get(key))
  return row && row.body ? row : null
}

/**
 * Remember a /metrics body under `key` for `day`. Only a COMPLETE answer is
 * worth keeping: a refusal (complete:false) cached for a day would show the
 * page "unavailable" all day after the mirror had recovered. Prunes rows older
 * than KEEP_DAYS while it is there.
 */
export async function writeCached(key, day, body, now = Date.now()) {
  if (!body || !body.available || !body.complete) return false
  const db = await openDb()
  if (!db) return false
  const ok = await request(db, 'readwrite', (store) => store.put({ key, day, savedAt: now, body }))
  pruneOld(db, day).catch(() => {})
  return ok !== null
}

async function pruneOld(db, today) {
  const cutoff = shiftDay(today, -KEEP_DAYS)
  const keys = await request(db, 'readonly', (store) => store.getAllKeys())
  for (const key of keys || []) {
    const day = String(key).split('|').pop()
    if (day && day < cutoff) await request(db, 'readwrite', (store) => store.delete(key))
  }
}

// YYYY-MM-DD plus/minus whole days, in the day key's own calendar.
function shiftDay(day, n) {
  const [y, m, d] = String(day).split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + n))
  return t.toISOString().slice(0, 10)
}

/** Test seam: forget the open database so a fake factory can be swapped in. */
export function _resetMetricsCache() {
  dbPromise = null
}
